/* eslint-disable no-nested-ternary */
import { InputNumber, Input, Radio, Switch, Space, notification } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import useQuotesContext from './QuotesContext';
import { getLegalOptions } from '../../utils/milaOptions';

const insuranceOptionsByActivity = {
  COA: ['investment_advisor_errors', 'insurance_intermediary_guarantee'],
  MIA: ['insurance_intermediary_guarantee', 'iobsp_mandate_professional'],
  IOBSP: [
    'insurance_intermediary_guarantee',
    'iobsp_broker_guarantee',
    'banking_solicitor_errors'
  ],
  MIOBSP: ['insurance_intermediary_guarantee', 'insurance_sub_agents_errors'],
  CGP_CIF: ['investment_advisor_errors'],
  AI: [
    'iobsp_broker_errors',
    'iobsp_exclusive_agent_errors',
    'iobsp_non_exclusive_agent_errors',
    'iobsp_mandate_professional',
    'insurance_intermediary_guarantee',
    'general_agents_and_brokers_errors',
    'insurance_sub_agents_errors',
    'real_estate_transaction_guarantee'
  ],
  ACI: []
};
const includedOptionsByActivity = {
  COA: [
    'operational_liability',
    'insurance_brokers',
    'iobsp_broker_errors',
    'iobsp_exclusive_agent_errors',
    'iobsp_non_exclusive_agent_errors',
    'iobsp_mandate_professional',
    'banking_solicitor_errors'
  ],
  MIA: ['operational_liability', 'insurance_sub_agents_errors'],
  IOBSP: [
    'operational_liability',
    'insurance_sub_agents_errors',
    'banking_and_finance_intermediary_professional',
    'borrower_insurance_intermediary_errors'
  ],
  MIOBSP: ['operational_liability', 'iobsp_mandate_professional'],
  CGP_CIF: ['operational_liability', 'wealth_and_financial_advisor_errors'],
  AI: ['operational_liability', 'real_estate_transaction_errors'],
  ACI: ['operational_liability', 'real_estate_transaction_errors']
};

const guaranteeLimitsByActivity = {
  COA: [2000000, 3000000],
  MIA: [2000000],
  IOBSP: [500000],
  MIOBSP: [500000],
  CGP_CIF: [600000],
  AI: [250000, 500000, 1000000, 2000000],
  ACI: [250000]
};

const allGuarantees = [
  'insurance_brokers',
  'operational_liability',
  'iobsp_broker_errors',
  'iobsp_exclusive_agent_errors',
  'iobsp_non_exclusive_agent_errors',
  'iobsp_mandate_professional',
  'banking_solicitor_errors',
  'investment_advisor_errors',
  'insurance_intermediary_guarantee',
  'real_estate_transaction_errors',
  'general_agents_and_brokers_errors',
  'insurance_sub_agents_errors',
  'real_estate_transaction_guarantee',
  'wealth_and_financial_advisor_errors',
  'banking_and_finance_intermediary_professional',
  'borrower_insurance_intermediary_errors',
  'iobsp_broker_guarantee'
];

export const getRCGLSTurnoverExemptionThreshold = (main_activity) => {
  if (main_activity) {
    switch (main_activity) {
      case 'COA':
      case 'IOBSP':
      case 'AI':
      case 'CGP_CIF':
        return 3000000;
      case 'MIA':
      case 'MIOBSP':
        return 200000;
      case 'ACI':
        return 250000;
      default:
        return 200000;
    }
  }
  return 200000;
};

const useRCGLSFields = (form) => {
  const { t } = useTranslation();
  const { exemptions, formValues, setFormValues, enums } = useQuotesContext();
  const [checkedVal, setCheckedVal] = useState('');
  const [displayLegalProtection, setDisplayLegalProtection] = useState(false);
  const isAmendment = window.location.href.search('amendment');

  const maxTurnover = getRCGLSTurnoverExemptionThreshold(
    formValues?.main_activity || 'ACI'
  );

  const handleLegalProtectionDisplayChange = (value) => {
    if (value) {
      setDisplayLegalProtection(true);
      form.setFieldsValue({
        legal_protection_choice: 1
      });
      setFormValues({
        ...formValues,
        legal_protection: true,
        extended_legal_protection: false
      });
    } else {
      form.setFieldsValue({
        legal_protection_choice: 0
      });
      setFormValues({
        ...formValues,
        legal_protection: false,
        extended_legal_protection: false,
        broker_fee: 0
      });
      setDisplayLegalProtection(false);
    }
  };

  const handleLegalProtectionChange = (value) => {
    const transformedData = {
      legal_protection: value >= 1,
      extended_legal_protection: value === 2
    };

    setFormValues({
      ...formValues,
      ...transformedData
    });
  };

  const displayedFields =
    insuranceOptionsByActivity[formValues?.main_activity] || [];

  const filteredOptions = guaranteeLimitsByActivity[
    formValues?.main_activity
  ] || [2000000];

  const conditionalFields = displayedFields.map((fieldName) => ({
    name: [fieldName],
    label: fieldName,
    tooltip: true,
    input: <PreRequisitesSwitch />,
    valuePropName: 'checked',
    step: 0
  }));

  const openNotificationWithIcon = (notificationType, value) => {
    if (value) {
      notification[notificationType]({
        message: 'Notification de dérogation',
        description: t('help.activities'),
        duration: 7,
        key: 'oneTime'
      });
    } else {
      notification[notificationType]({
        message: t('contracts.form.contribution_adjustment_message'),
        key: 'oneTime'
      });
    }
  };

  useEffect(() => {
    if (exemptions.includes(checkedVal)) {
      openNotificationWithIcon('warning', 'value');
    } // eslint-disable-next-line
  }, [exemptions.length]);

  const rcglsFields = [
    {
      name: ['main_activity'],
      rules: [{ required: true }],
      input: (
        <Radio.Group
          buttonStyle="solid"
          onChange={(e) => {
            const activity = e.target.value;
            const included = includedOptionsByActivity[activity] || {};
            const newValues = Object.fromEntries(
              allGuarantees.map((g) => [g, included.includes(g)])
            );
            const defaultLimit =
              guaranteeLimitsByActivity[activity]?.[0] ?? null;
            setFormValues({
              ...formValues,
              main_activity: activity,
              guarantee_limit: defaultLimit,
              ...newValues
            });
            form.setFieldsValue({
              ...newValues,
              guarantee_limit: defaultLimit
            });
          }}
        >
          {Object.entries(enums?.MainActivities || {}).map(([key, value]) => (
            <Radio.Button value={key} key={key}>
              <Space orientation="horizontal">{value}</Space>
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['turnover'],
      tooltip: {
        name: 'turnover_params',
        params: { max: maxTurnover }
      },
      input: <InputNumber min={0} style={{ width: 120 }} />,
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      help: exemptions.includes('turnover') ? t('help.exemptions') : null,
      step: 0
    },
    {
      name: ['guarantee_limit'],
      tooltip: true,
      tooltipName: 'guarantee_limit_rcgls',
      label: 'guarantee_ceiling',
      rules: [{ required: true }],
      input: (
        <Radio.Group buttonStyle="solid">
          {filteredOptions.map((limit) => (
            <Radio.Button key={limit} value={limit}>
              {`${limit.toLocaleString()}€`}
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      help: exemptions.includes('guarantee_limit')
        ? t('help.exemptions')
        : null,
      step: 0
    },
    ...conditionalFields,
    {
      name: ['display_legal_protection'],
      tooltip: true,
      tooltipName: 'legal_protection_choice',
      input: (
        <Switch
          checkedChildren={t('quotes.form.switch.yes')}
          unCheckedChildren={t('quotes.form.switch.no')}
          onChange={(value) => handleLegalProtectionDisplayChange(value)}
        />
      ),
      step: 0
    },
    ...(displayLegalProtection
      ? [
          {
            name: ['legal_protection_choice'],
            input: (
              <Radio.Group
                buttonStyle="solid"
                onChange={(e) => handleLegalProtectionChange(e.target.value)}
              >
                {getLegalOptions()
                  .filter((option) => option.value > 0)
                  .map((option) => (
                    <Radio.Button key={option.value} value={option.value}>
                      {t(`contracts.form.options.${option.label}`)}
                    </Radio.Button>
                  ))}
              </Radio.Group>
            ),
            help: exemptions.includes('legal_protection')
              ? t('help.exemptions')
              : null,
            step: 0
          }
        ]
      : []),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['no_activity_usa_canada'],
      step: 1
    },
    {
      name: ['not_listed_in_eu_sanctions'],
      step: 1
    },
    {
      name: ['accurate_and_truthful_declarations'],
      step: 1
    },
    {
      name: ['report_change_of_situation'],
      step: 1
    },
    {
      name: ['no_acpr_sanctions'],
      step: 1
    },
    {
      name: ['no_foreign_branch_or_subsidiary'],
      step: 1
    },
    {
      name: ['no_problems_last_5_years'],
      step: 1
    },
    {
      name: ['no_known_risk_facts'],
      step: 1
    },
    {
      name: ['orias_required_for_guarantee'],
      step: 1
    },
    {
      name: ['registration_required_for_guarantee'],
      step: 1
    },
    {
      name: ['already_resiliated_by_assurmax'],
      step: 1
    },
    {
      name: ['legal_and_regulatory_compliance'],
      step: 1
    },
    {
      name: ['politically_exposed_person'],
      step: 1
    }
  ];

  // ACI
  if (formValues?.main_activity === 'ACI') {
    rcglsFields.push(
      { name: ['multiple_principals'], step: 1 },
      { name: ['registered_commercial_agent'], step: 1 },
      ...(formValues?.registered_commercial_agent === true
        ? [
            {
              name: ['professional_card_number'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      ...(formValues?.registered_commercial_agent === false
        ? [{ name: ['in_process_cci'], step: 1 }]
        : []),
      {
        name: ['issuing_chamber'],
        step: 1,
        input: <Input />,
        rules: [{ required: true }]
      },
      {
        name: ['agency_contact_details'],
        step: 1,
        input: <Input />,
        rules: [{ required: true }]
      }
    );
  }

  // CGP_CIF
  if (formValues?.main_activity === 'CGP_CIF') {
    rcglsFields.push(
      { name: ['no_financial_engineering'], step: 1 },
      { name: ['real_estate_tax_reduction_activity'], step: 1 },
      { name: ['no_asset_management_for_third_parties'], step: 1 },
      { name: ['no_miscellaneous_goods_intermediation'], step: 1 },
      { name: ['no_gestion_patrimoine_without_cif'], step: 1 }
    );
  }

  // IOBSP
  if (formValues?.main_activity === 'IOBSP') {
    rcglsFields.push(
      { name: ['mandataires_to_cover'], step: 1 },
      { name: ['orias_compliance_insurance'], step: 1 },
      ...(formValues?.orias_compliance_insurance === true
        ? [
            {
              name: ['orias_number'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['accessory_activity_ia_mia_dfb'], step: 1 },
      { name: ['accessory_activity_ia_mia'], step: 1 },
      { name: ['accessory_activity_dfb'], step: 1 }
    );
  }

  // COA
  if (formValues?.main_activity === 'COA') {
    rcglsFields.push(
      { name: ['delegation_or_mandate'], step: 1 },
      { name: ['accounts_verified_by_accountant'], step: 1 },
      { name: ['mandataires_to_cover_mias'], step: 1 },
      { name: ['no_risky_insurance_contexts'], step: 1 },
      { name: ['reinsurance_broker'], step: 1 },
      { name: ['orias_compliance_assurance'], step: 1 },
      ...(formValues?.orias_compliance_assurance === true
        ? [
            {
              name: ['orias_number'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['accessory_activity_iobsp_dfb_cif'], step: 1 },
      ...(formValues?.accessory_activity_iobsp_dfb_cif === true
        ? [
            { name: ['accessory_activity_iobsp'], step: 1 },
            { name: ['accessory_activity_dfb'], step: 1 },
            { name: ['accessory_activity_cif'], step: 1 }
          ]
        : [])
    );
  }

  // AI
  if (formValues?.main_activity === 'AI') {
    rcglsFields.push(
      { name: ['no_foreign_branch'], step: 1 },
      ...(formValues?.no_foreign_branch === true
        ? [
            {
              name: ['legal_representative_name'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['no_property_management_activity'], step: 1 },
      { name: ['real_estate_tax_reduction_activity_ai'], step: 1 },
      { name: ['wants_financial_guarantee_no_funds'], step: 1 },
      ...(formValues?.wants_financial_guarantee_no_funds === true
        ? [
            {
              name: ['financial_guarantee_amount_no_funds'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['accounts_verified_by_accountant'], step: 1 },
      { name: ['accessory_activity_ia_mia_or_iobsp_miobsp'], step: 1 },
      ...(formValues?.accessory_activity_ia_mia_or_iobsp_miobsp === true
        ? [
            { name: ['accessory_activity_ia_mia_alt'], step: 1 },
            { name: ['accessory_activity_iobsp_miobsp'], step: 1 }
          ]
        : [])
    );
  }

  if (formValues?.main_activity === 'MIOBSP') {
    rcglsFields.push(
      { name: ['single_mandate_iobsp'], step: 1 },
      {
        name: ['mandant_details'],
        step: 1,
        input: <Input />,
        rules: [{ required: true }]
      },
      { name: ['orias_compliance_miobsp'], step: 1 },
      ...(formValues?.orias_compliance_miobsp === true
        ? [
            {
              name: ['orias_number'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['only_france_based_clients'], step: 1 },
      { name: ['no_us_canada_bank_activity'], step: 1 }
    );
  }

  // MIA
  if (formValues?.main_activity === 'MIA') {
    rcglsFields.push(
      { name: ['single_mandate_insurance'], step: 1 },
      { name: ['orias_compliance_bank'], step: 1 },
      ...(formValues?.orias_compliance_bank === true
        ? [
            {
              name: ['orias_number'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['accounts_verified_by_accountant'], step: 1 },
      { name: ['only_france_based_clients'], step: 1 },
      { name: ['risky_mandant_relationships'], step: 1 },
      { name: ['maritime_insurance'], step: 1 },
      ...(formValues?.maritime_insurance === true
        ? [
            {
              name: ['maritime_percentage'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : []),
      { name: ['construction_insurance'], step: 1 },
      ...(formValues?.construction_insurance === true
        ? [{ name: ['construction_insurance_company_check'], step: 1 }]
        : []),
      ...(formValues?.construction_insurance === true
        ? [{ name: ['construction_project_check'], step: 1 }]
        : []),
      ...(formValues?.construction_insurance === true
        ? [
            {
              name: ['construction_revenue_percentage'],
              step: 1,
              input: <Input />,
              rules: [{ required: true }]
            }
          ]
        : [])
    );
  }

  return {
    rcglsFields: rcglsFields.map((f) => {
      if (f.step === 1 && !f.input)
        return {
          ...f,
          input: (
            <PreRequisitesSwitch
              onChange={(val) => {
                setFormValues({
                  ...formValues,
                  [f.name[0]]: val
                });
                setCheckedVal(f.name[0]);
              }}
            />
          ),
          labelAlign: 'left',
          valuePropName: 'checked',
          withDivider: true,
          layout: preRequisitesLayout
        };
      return f;
    })
  };
};

export default useRCGLSFields;
